import { createContext, useContext, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Check, X } from "lucide-react";
import { useSiteSettings } from "@/context/SiteSettingsContext";

const TOKEN_KEY = "bk_admin_token";
const Ctx = createContext({ editMode: false, openEditor: () => {} });

export function useTextEdit() {
  return useContext(Ctx);
}

/** Returns the override for `k` or the default `d`. For non-tappable
 * strings (placeholders, dynamic labels). */
export function useT(k, d = "") {
  const { settings } = useSiteSettings();
  const v = (settings.text_overrides || {})[k];
  return v !== undefined ? v : d;
}

/** Editable text node. `k` + `d` = override key + default copy; or pass
 * `field` to edit a real site_settings field in place. Renders a plain
 * fragment outside edit mode (zero layout impact). */
export function T({ k, d = "", field }) {
  const { settings } = useSiteSettings();
  const { editMode, openEditor } = useTextEdit();
  const raw = field ? settings[field] : (settings.text_overrides || {})[k];
  const value = raw === undefined || raw === null || raw === "" ? d : raw;
  if (!editMode) return <>{value}</>;
  return (
    <span
      role="button"
      tabIndex={0}
      data-testid={`tx-edit-${field || k}`}
      className="tx-edit"
      title="Tap to edit this text"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        openEditor({ k, d, field, value });
      }}
    >
      {value}
    </span>
  );
}

export function TextEditProvider({ children }) {
  const { settings, refresh } = useSiteSettings();
  const [editMode, setEditMode] = useState(() => {
    if (typeof window === "undefined") return false;
    const qs = new URLSearchParams(window.location.search);
    return qs.get("edit-text") === "1" && !!localStorage.getItem(TOKEN_KEY);
  });
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const overrides = settings.text_overrides || {};

  const openEditor = (target) => {
    setEditing(target);
    setDraft(target.value ?? "");
  };

  const persist = async (body) => {
    const token = localStorage.getItem(TOKEN_KEY);
    setSaving(true);
    try {
      await api.put("/site-settings", body, {
        headers: { "X-Admin-Token": token },
      });
      await refresh();
      toast.success("Text updated");
      setEditing(null);
    } catch (err) {
      toast.error(
        err?.response?.status === 401
          ? "Session expired — log back into Admin, then reopen edit mode"
          : "Save failed — try again"
      );
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    if (!editing) return;
    if (editing.field) return persist({ [editing.field]: draft });
    const next = { ...overrides };
    if (draft === editing.d || draft.trim() === "") delete next[editing.k];
    else next[editing.k] = draft;
    return persist({ text_overrides: next });
  };

  const resetToDefault = () => {
    const next = { ...overrides };
    delete next[editing.k];
    return persist({ text_overrides: next });
  };

  const exitEditMode = () => {
    setEditMode(false);
    const url = new URL(window.location.href);
    url.searchParams.delete("edit-text");
    window.history.replaceState({}, "", url.pathname + url.search);
  };

  return (
    <Ctx.Provider value={{ editMode, openEditor }}>
      {children}
      {editMode && (
        <div
          data-testid="text-edit-toolbar"
          className="fixed bottom-3 left-1/2 -translate-x-1/2 z-[95] flex items-center gap-3 bg-stone-900 text-white pl-4 pr-2 py-2 rounded-full shadow-2xl border border-stone-700 max-w-[95vw]"
        >
          <Pencil className="w-3.5 h-3.5 text-brand-yellow shrink-0" />
          <span className="text-xs font-bold whitespace-nowrap truncate">
            Text Edit Mode — tap any outlined text
          </span>
          <button
            onClick={exitEditMode}
            data-testid="text-edit-done-btn"
            className="bg-brand-yellow text-stone-900 px-4 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest hover:brightness-105 shrink-0"
          >
            Done
          </button>
        </div>
      )}
      {editing && (
        <div
          data-testid="text-edit-modal"
          className="fixed inset-0 z-[100] bg-stone-900/70 flex items-center justify-center p-4"
          onClick={() => !saving && setEditing(null)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md p-5 space-y-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div className="font-display text-brand-red text-xl">Edit Text</div>
              <button
                onClick={() => setEditing(null)}
                data-testid="text-edit-cancel-x"
                className="p-1 rounded-full hover:bg-stone-100"
              >
                <X className="w-4 h-4 text-stone-500" />
              </button>
            </div>
            <textarea
              autoFocus
              rows={3}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              data-testid="text-edit-input"
              className="w-full px-3 py-2.5 rounded-lg border-2 border-stone-300 outline-none focus:border-brand-red text-sm resize-y"
            />
            {!editing.field && editing.d && (
              <div className="text-[11px] text-stone-400 leading-snug">
                Default: “{editing.d}”
              </div>
            )}
            <div className="flex gap-2">
              {!editing.field && overrides[editing.k] !== undefined && (
                <button
                  onClick={resetToDefault}
                  disabled={saving}
                  data-testid="text-edit-reset-btn"
                  className="px-3 py-2 rounded-full text-xs uppercase tracking-widest font-bold text-stone-600 hover:bg-stone-100 disabled:opacity-40"
                >
                  Reset
                </button>
              )}
              <button
                onClick={() => setEditing(null)}
                disabled={saving}
                data-testid="text-edit-cancel-btn"
                className="flex-1 border-2 border-stone-300 text-stone-600 py-2 rounded-full text-xs uppercase tracking-widest font-bold hover:border-stone-500 disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="text-edit-save-btn"
                className="flex-1 bg-brand-red text-white py-2 rounded-full text-xs uppercase tracking-widest font-black hover:bg-red-800 disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                <Check className="w-3.5 h-3.5" />
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}
